import type { Concept, Mashup, Synonym, AvailableDomain, NameEvaluation } from '../store/sessionStore';

interface ConceptResponse {
  concepts: Array<Pick<Concept, 'label' | 'votes' | 'source'>>;
}

type SynonymCandidate = Omit<Synonym, 'id' | 'votes' | 'dismissed'> & {
  votes?: number;
  dismissed?: boolean;
};

interface SynonymResponse {
  synonyms: SynonymCandidate[];
}

type MashupCandidate = Omit<Mashup, 'id' | 'votes' | 'domainStatus' | 'evaluation'> & {
  votes?: number;
};

interface MashupResponse {
  mashups: MashupCandidate[];
}

interface ProjectNameResponse {
  name: string;
}

interface NameScore extends NameEvaluation {
  name: string;
}

interface ScoringResponse {
  scores: NameScore[];
}

const USE_MOCK_AGENT = (import.meta.env.VITE_USE_MOCK_AGENT ?? 'false') === 'true';
const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? '/api').replace(/\/$/, '');

const mockDelay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const mockAgent = {
  async generateConcepts(brief: string, existing: string[] = []): Promise<ConceptResponse> {
    await mockDelay(400);
    const seed = brief || 'innovation platform';
    const sample = Array.from(new Set(seed.split(/\W+/))).filter(Boolean);
    const concepts = sample.slice(0, 6).map((word) => ({
      label: word.replace(/^[a-z]/, (char) => char.toUpperCase()),
      votes: 0,
      source: 'ai' as const
    })).filter(
      (concept) => !existing.some((label) => label.toLowerCase() === concept.label.toLowerCase())
    );
    if (concepts.length === 0) {
      ['Spark', 'Launch', 'Nova', 'Pulse'].forEach((label) => {
        if (!existing.some((item) => item.toLowerCase() === label.toLowerCase())) {
          concepts.push({ label, votes: 0, source: 'ai' });
        }
      });
    }
    return { concepts };
  },
  async generateSynonyms(concept: Concept): Promise<SynonymResponse> {
    await mockDelay(250);
    const variants = [
      `${concept.label} Lab`,
      `${concept.label} Hub`,
      `${concept.label} Works`,
      `${concept.label} Forge`
    ];
    return {
      synonyms: variants.map((label) => ({
        conceptId: concept.id,
        label,
        source: 'ai' as const
      }))
    };
  },
  async generateMashups(concepts: Concept[], synonyms: Synonym[]): Promise<MashupResponse> {
    await mockDelay(500);
    const mashups: MashupCandidate[] = [];
    const topConcepts = concepts.slice(0, 3);
    topConcepts.forEach((concept) => {
      const relatedSynonyms = synonyms.filter((syn) => syn.conceptId === concept.id);
      relatedSynonyms.slice(0, 3).forEach((synonym) => {
        mashups.push({
          label: `${concept.label}${synonym.label.replace(concept.label, '')}`.trim(),
          votes: 0,
          sources: [
            { type: 'concept', id: concept.id },
            { type: 'synonym', id: synonym.id }
          ],
          availableDomains: []
        });
      });
    });
    if (mashups.length === 0 && concepts[0]) {
      mashups.push({
        label: `${concepts[0].label}ly`,
        votes: 0,
        sources: [{ type: 'concept', id: concepts[0].id }],
        availableDomains: []
      });
    }
    return { mashups };
  },
  async checkDomain(name: string): Promise<{ status: Mashup['domainStatus'] }> {
    await mockDelay(200);
    return { status: name.length % 3 === 0 ? 'taken' : 'available' };
  },
  async generateProjectName(brief: string): Promise<ProjectNameResponse> {
    await mockDelay(150);
    const tokens = brief
      .replace(/[^a-zA-Z0-9\s]/g, ' ')
      .split(/\s+/)
      .filter(Boolean);
    const name = tokens.slice(0, 4).join(' ') || 'Untitled Project';
    return { name };
  }
};

async function httpPost<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
  if (!response.ok) {
    const message = await response.text();
    throw new Error(`Request failed (${response.status}): ${message}`);
  }
  return (await response.json()) as T;
}

export const agent = {
  async generateConcepts(brief: string, existing: string[] = [], styles: string[] = []) {
    if (USE_MOCK_AGENT) {
      return mockAgent.generateConcepts(brief, existing);
    }
    return httpPost<ConceptResponse>('/concepts/generate', { brief, existing, styles });
  },
  async generateSynonyms(concept: Concept, styles: string[] = []) {
    if (USE_MOCK_AGENT) {
      return mockAgent.generateSynonyms(concept);
    }
    return httpPost<SynonymResponse>('/synonyms/generate', { concept, styles });
  },
  async generateMashups(concepts: Concept[], synonyms: Synonym[], styles: string[] = []) {
    if (USE_MOCK_AGENT) {
      return mockAgent.generateMashups(concepts, synonyms);
    }
    return httpPost<MashupResponse>('/mashups/generate', { concepts, synonyms, styles });
  },
  async checkDomain(name: string) {
    if (USE_MOCK_AGENT) {
      return mockAgent.checkDomain(name);
    }
    return httpPost<{ status: Mashup['domainStatus'] }>('/domains/check', { name });
  },
  async searchDomains(name: string) {
    return httpPost<{ domains: AvailableDomain[] }>('/domains/search', { name });
  },
  async generateProjectName(brief: string, styles: string[] = []) {
    if (USE_MOCK_AGENT) {
      return mockAgent.generateProjectName(brief);
    }
    return httpPost<ProjectNameResponse>('/projects/name', { brief, styles });
  },
  async scoreNames(brief: string, names: string[]) {
    return httpPost<ScoringResponse>('/scoring/evaluate', { brief, names });
  }
};
